import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import './catalog.css';

const CURATED_BY_REGEX = /^curated by\s+/i;
const CREATED_BY_REGEX = /^created by\s+/i;

const extractCuratorName = (label) => {
  if (!label || typeof label !== 'string') {
    return '';
  }
  const trimmed = label.trim();
  if (!trimmed) {
    return '';
  }
  if (CURATED_BY_REGEX.test(trimmed)) {
    return trimmed.replace(CURATED_BY_REGEX, '').trim();
  }
  if (CREATED_BY_REGEX.test(trimmed)) {
    return '';
  }
  return trimmed;
};

const DEFAULT_FORM = {
  title: '',
  curator: '',
  description: '',
  tagInput: '',
  tags: [],
  artworks: [],
  visibility: 'private',
};

const MIN_ARTWORKS = 1;
const INITIAL_VISIBLE_ARTWORKS = 9;
const ARTWORK_INCREMENT = 9;

function createPlaceholderArtwork(seedPrefix, existingCount) {
  const seed = `${seedPrefix}-${Date.now()}-${existingCount + 1}`;
  return {
    id: `placeholder-${seed}`,
    url: `https://picsum.photos/seed/${encodeURIComponent(seed)}/720/480`,
    title: `Artwork ${existingCount + 1}`,
    tags: [],
  };
}

const CollectionComposer = ({
  isOpen,
  onCancel,
  onSubmit,
  availableArtworks,
  initialCollection,
  mode,
}) => {
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [error, setError] = useState('');
  const [visibleArtworkCount, setVisibleArtworkCount] = useState(INITIAL_VISIBLE_ARTWORKS);
  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (!isOpen) {
      setFormState({ ...DEFAULT_FORM });
      setError('');
      setVisibleArtworkCount(INITIAL_VISIBLE_ARTWORKS);
      return;
    }

    if (initialCollection) {
      const initialCuratorName =
        (initialCollection.curatorName && initialCollection.curatorName.trim()) ||
        extractCuratorName(initialCollection.curator);

      setFormState({
        title: initialCollection.title || '',
        curator: initialCuratorName || '',
        description: initialCollection.description || '',
        tagInput: '',
        tags: Array.isArray(initialCollection.tags) ? initialCollection.tags.slice() : [],
        artworks: Array.isArray(initialCollection.artworks)
          ? initialCollection.artworks.map((artwork) => ({
              ...artwork,
              tags: Array.isArray(artwork.tags) ? artwork.tags.slice() : [],
            }))
          : [],
        visibility: initialCollection.visibility === 'public' ? 'public' : 'private',
      });
    } else {
      setFormState({ ...DEFAULT_FORM });
    }

    setError('');
    setVisibleArtworkCount(INITIAL_VISIBLE_ARTWORKS);
  }, [initialCollection, isOpen]);

  useEffect(() => {
    setVisibleArtworkCount((count) => {
      if (availableArtworks.length === 0) {
        return INITIAL_VISIBLE_ARTWORKS;
      }
      return Math.min(count, availableArtworks.length);
    });
  }, [availableArtworks.length]);

  useEffect(() => {
    setFormState((prev) => {
      if (!Array.isArray(prev.artworks) || prev.artworks.length === 0) {
        return prev;
      }

      const availableIds = new Set(
        availableArtworks
          .map((artwork) => (artwork && artwork.id ? String(artwork.id) : ''))
          .filter(Boolean),
      );

      const nextArtworks = prev.artworks.filter((artwork) => {
        if (!artwork || !artwork.id) {
          return false;
        }

        if (artwork.collectionId) {
          return true;
        }

        const idString = String(artwork.id);
        if (!idString.toLowerCase().startsWith('history-')) {
          return true;
        }

        return availableIds.has(idString);
      });

      if (nextArtworks.length === prev.artworks.length) {
        return prev;
      }

      return {
        ...prev,
        artworks: nextArtworks,
      };
    });
  }, [availableArtworks]);

  const canSubmit = useMemo(() => {
    return formState.title.trim() && formState.artworks.length >= MIN_ARTWORKS;
  }, [formState.artworks.length, formState.title]);

  const toTitleCase = (label) =>
    label
      .split(/\s+/)
      .map((word) => {
        if (!word) {
          return '';
        }
        const lower = word.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ')
      .trim();

  const handleChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddTag = () => {
    const trimmed = formState.tagInput.trim();
    if (!trimmed) {
      handleChange('tagInput', '');
      return;
    }
    const titleCaseTag = toTitleCase(trimmed);
    if (formState.tags.some((tag) => tag.toLowerCase() === titleCaseTag.toLowerCase())) {
      handleChange('tagInput', '');
      return;
    }
    setFormState((prev) => ({
      ...prev,
      tags: [...prev.tags, titleCaseTag],
      tagInput: '',
    }));
  };

  const handleRemoveTag = (tag) => {
    setFormState((prev) => ({
      ...prev,
      tags: prev.tags.filter((item) => item !== tag),
    }));
  };

  const handleAddPlaceholder = () => {
    setFormState((prev) => ({
      ...prev,
      artworks: [createPlaceholderArtwork('collection', prev.artworks.length), ...prev.artworks],
    }));
  };

  const handleRemoveArtwork = (artworkId) => {
    setFormState((prev) => ({
      ...prev,
      artworks: prev.artworks.filter((artwork) => artwork.id !== artworkId),
    }));
  };

  const handleToggleArtworkSelection = (artwork) => {
    if (!artwork || !artwork.id) {
      return;
    }

    setFormState((prev) => {
      const alreadySelected = prev.artworks.some((item) => item.id === artwork.id);

      if (alreadySelected) {
        return {
          ...prev,
          artworks: prev.artworks.filter((item) => item.id !== artwork.id),
        };
      }

      const normalizedArtwork = {
        id: artwork.id,
        url: artwork.url,
        title: artwork.title || 'Artwork',
        prompt: artwork.prompt,
        collectionId: artwork.collectionId,
        tags: Array.isArray(artwork.tags)
          ? artwork.tags.map((tag) => toTitleCase(tag)).filter(Boolean)
          : [],
      };

      return {
        ...prev,
        artworks: [normalizedArtwork, ...prev.artworks],
      };
    });
  };

  const isArtworkSelected = useCallback(
    (artworkId) => formState.artworks.some((item) => item.id === artworkId),
    [formState.artworks],
  );

  const displayedArtworks = useMemo(
    () => availableArtworks.slice(0, visibleArtworkCount),
    [availableArtworks, visibleArtworkCount],
  );

  const handleShowMoreArtworks = () => {
    setVisibleArtworkCount((count) => Math.min(count + ARTWORK_INCREMENT, availableArtworks.length));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) {
      setError('Add a title and at least one artwork to save your collection.');
      return;
    }

    setError('');
    if (onSubmit) {
      onSubmit({
        id: initialCollection ? initialCollection.id : undefined,
        title: formState.title,
        curator: formState.curator,
        description: formState.description,
        tags: formState.tags,
        artworks: formState.artworks,
        visibility: formState.visibility,
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="catalog-composer__backdrop" role="presentation" onClick={onCancel}>
      <div
        className="catalog-composer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="collection-composer-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="catalog-composer__header">
          <div>
            <h2 id="collection-composer-title">
              {isEditMode ? 'Update collection' : 'Build a new collection'}
            </h2>
            <p>
              {isEditMode
                ? 'Refine your collection by adjusting its details, tags, or artwork selections.'
                : 'Create a collection by adding a title, optional tags, and at least one artwork.'}
            </p>
          </div>
          <button type="button" className="catalog-composer__close" onClick={onCancel}>
            <span className="sr-only">Close</span>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <form className="catalog-composer__form" onSubmit={handleSubmit}>
          <label className="catalog-composer__field">
            <span>Collection title</span>
            <input
              type="text"
              value={formState.title}
              onChange={(event) => handleChange('title', event.target.value)}
              placeholder="E.g. Campaign launch moodboard"
              required
            />
          </label>
          <label className="catalog-composer__field">
            <span>Curator name</span>
            <input
              type="text"
              value={formState.curator}
              onChange={(event) => handleChange('curator', event.target.value)}
              placeholder="E.g. Dana Williams"
            />
          </label>
          <label className="catalog-composer__field">
            <span>Description</span>
            <textarea
              value={formState.description}
              onChange={(event) => handleChange('description', event.target.value)}
              placeholder="Describe the focus or story behind this collection"
              rows={3}
            />
          </label>
          <fieldset className="catalog-composer__field catalog-composer__visibility">
            <legend>Visibility</legend>
            <div className="catalog-composer__visibility-options">
              <label
                className={`catalog-composer__visibility-option${
                  formState.visibility === 'private' ? ' is-active' : ''
                }`}
              >
                <input
                  type="radio"
                  name="collection-visibility"
                  value="private"
                  checked={formState.visibility === 'private'}
                  onChange={() => handleChange('visibility', 'private')}
                />
                <span className="catalog-composer__visibility-label">
                  <strong>Private</strong>
                  <small>Only visible in your account.</small>
                </span>
              </label>
              <label
                className={`catalog-composer__visibility-option${
                  formState.visibility === 'public' ? ' is-active' : ''
                }`}
              >
                <input
                  type="radio"
                  name="collection-visibility"
                  value="public"
                  checked={formState.visibility === 'public'}
                  onChange={() => handleChange('visibility', 'public')}
                />
                <span className="catalog-composer__visibility-label">
                  <strong>Public</strong>
                  <small>Published as a platform template for all users.</small>
                </span>
              </label>
            </div>
          </fieldset>
          <div className="catalog-composer__field">
            <span>Tags</span>
            <div className="catalog-composer__tag-row">
              <input
                type="text"
                value={formState.tagInput}
                onChange={(event) => handleChange('tagInput', event.target.value)}
                placeholder="Tag name"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <button type="button" onClick={handleAddTag}>
                Add
              </button>
            </div>
            {!!formState.tags.length && (
              <ul className="catalog-composer__tag-list">
                {formState.tags.map((tag) => (
                  <li key={tag}>
                    <span>{tag}</span>
                    <button type="button" onClick={() => handleRemoveTag(tag)}>
                      <span className="sr-only">Remove {tag}</span>
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="catalog-composer__field">
            <span>Choose artworks</span>
            <p className="catalog-composer__hint">
              Tap an image to add it to your collection. Selected items show a checkmark.
            </p>
            {availableArtworks.length > 0 ? (
              <>
                <div className="catalog-composer__artwork-library">
                  {displayedArtworks.map((artwork) => {
                    const selected = isArtworkSelected(artwork.id);
                    return (
                      <button
                        key={artwork.id}
                        type="button"
                        className={`catalog-composer__artwork-option${
                          selected ? ' is-selected' : ''
                        }`}
                        onClick={() => handleToggleArtworkSelection(artwork)}
                        aria-pressed={selected}
                        aria-label={`${
                          selected ? 'Remove' : 'Add'
                        } ${artwork.title || 'artwork'} from collection`}
                        title={artwork.prompt || artwork.title || 'Artwork preview'}
                      >
                        <img
                          src={artwork.url}
                          alt={artwork.title || artwork.prompt || 'Artwork preview'}
                          loading="lazy"
                        />
                        {selected && (
                          <span className="catalog-composer__artwork-option-check" aria-hidden="true">
                            <i className="fa-solid fa-check" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {availableArtworks.length > visibleArtworkCount && (
                  <button
                    type="button"
                    className="catalog-composer__show-more"
                    onClick={handleShowMoreArtworks}
                  >
                    More...
                  </button>
                )}
              </>
            ) : (
              <p className="catalog-composer__empty catalog-composer__empty--subtle">
                No catalog imagery available yet. Add placeholders to mock up your collection.
              </p>
            )}
            <div className="catalog-composer__artwork-actions catalog-composer__artwork-row">
              <button type="button" onClick={handleAddPlaceholder}>
                Add placeholder
              </button>
            </div>
            <p className="catalog-composer__hint">
              Use the placeholder option to quickly generate sample imagery for demos.
            </p>
            {formState.artworks.length > 0 ? (
              <div className="catalog-composer__artworks">
                {formState.artworks.map((artwork) => {
                  return (
                    <figure
                      key={artwork.id}
                      title={artwork.prompt || artwork.title || 'Artwork preview'}
                    >
                      <div className="catalog-composer__artwork-preview">
                        <img
                          src={artwork.url}
                          alt={artwork.title || artwork.prompt || 'Artwork preview'}
                          loading="lazy"
                        />
                        <button
                          type="button"
                          className="catalog-composer__artwork-remove"
                          onClick={() => handleRemoveArtwork(artwork.id)}
                        >
                          <span className="sr-only">Remove artwork</span>
                          <i className="fa-solid fa-trash" aria-hidden="true" />
                        </button>
                      </div>
                      <figcaption>
                        {Array.isArray(artwork.tags) && artwork.tags.length > 0 && (
                          <ul className="catalog-composer__tag-list catalog-composer__tag-list--inline">
                            {artwork.tags.map((tag) => (
                              <li key={`${artwork.id}-${tag}`}>
                                <span>{tag}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            ) : (
              <p className="catalog-composer__empty">No artwork selected yet.</p>
            )}
          </div>
          {error && (
            <div className="catalog-composer__error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
          <footer className="catalog-composer__footer">
            <button type="button" onClick={onCancel} className="catalog-composer__secondary">
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit} className="catalog-composer__primary">
              {isEditMode ? 'Update collection' : 'Save collection'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

CollectionComposer.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  availableArtworks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
      prompt: PropTypes.string,
      collectionId: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  initialCollection: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    curator: PropTypes.string,
    curatorName: PropTypes.string,
    description: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    artworks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        title: PropTypes.string,
        prompt: PropTypes.string,
        collectionId: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
    visibility: PropTypes.oneOf(['private', 'public']),
  }),
  mode: PropTypes.oneOf(['create', 'edit']),
};

CollectionComposer.defaultProps = {
  isOpen: false,
  onCancel: undefined,
  onSubmit: undefined,
  availableArtworks: [],
  initialCollection: null,
  mode: 'create',
};

export default CollectionComposer;
